import { Plus, Trash2 } from "lucide-react";
import { Button, Compact, Input } from "~/components/ui";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { pairsToRecord as toRecord, recordsEqual } from "~/utils/key-value-utils";

interface KeyValuePair {
  id: number;
  key: string;
  value: string;
}

// Module-level counter: row ids only need uniqueness, and a ref-based counter
// cannot be read from render (react-hooks/refs)
let pairIdCounter = 0;

function toPairs(record?: Record<string, string>): KeyValuePair[] {
  return record ? Object.entries(record).map(([k, v]) => ({ id: ++pairIdCounter, key: k, value: v })) : [];
}

interface KeyValueEditorProps {
  value?: Record<string, string>;
  onChange?: (value: Record<string, string> | undefined) => void;
  keyPlaceholder?: string;
  valuePlaceholder?: string;
  addLabel?: string;
}

export function KeyValueEditor({
  value,
  onChange,
  keyPlaceholder = "/prefix/",
  valuePlaceholder = "VALUE",
  addLabel,
}: KeyValueEditorProps) {
  const { t } = useTranslation("admin");

  const [localPairs, setLocalPairs] = useState<KeyValuePair[]>(() => toPairs(value));

  // Sync external value changes (form reset, template load, modal reopen)
  // with the render-time adjustment pattern. Content comparison instead of an
  // "internal change" flag: the flag could stay armed when onChange produced
  // the same prop value (e.g. rows with an empty key), silently swallowing
  // the NEXT real external sync. The identity guard prevents render loops on
  // values whose round-trip normalizes (trimmed keys).
  const [lastSyncedValue, setLastSyncedValue] = useState(value);
  if (value !== lastSyncedValue) {
    setLastSyncedValue(value);
    if (!recordsEqual(toRecord(localPairs), value)) {
      setLocalPairs(toPairs(value));
    }
  }

  const syncToParent = useCallback(
    (pairs: KeyValuePair[]) => {
      onChange?.(toRecord(pairs));
    },
    [onChange]
  );

  const handleAdd = () => {
    setLocalPairs((prev) => [...prev, { id: ++pairIdCounter, key: "", value: "" }]);
  };

  const handleRemove = useCallback(
    (id: number) => {
      setLocalPairs((prev) => {
        const newPairs = prev.filter((p) => p.id !== id);
        syncToParent(newPairs);
        return newPairs;
      });
    },
    [syncToParent]
  );

  const handleKeyChange = useCallback(
    (id: number, newKey: string) => {
      setLocalPairs((prev) => {
        const newPairs = prev.map((p) => (p.id === id ? { ...p, key: newKey } : p));
        syncToParent(newPairs);
        return newPairs;
      });
    },
    [syncToParent]
  );

  const handleValueChange = useCallback(
    (id: number, newValue: string) => {
      setLocalPairs((prev) => {
        const newPairs = prev.map((p) => (p.id === id ? { ...p, value: newValue } : p));
        syncToParent(newPairs);
        return newPairs;
      });
    },
    [syncToParent]
  );

  return (
    <div className="space-y-2">
      {localPairs.map((pair) => (
        <Compact key={pair.id} className="w-full">
          <Input
            value={pair.key}
            onChange={(e) => handleKeyChange(pair.id, e.target.value)}
            placeholder={keyPlaceholder}
            aria-label={t("schemaAdmin.keyValue.key")}
            className="font-mono flex-1"
            style={{ width: "45%" }}
          />
          <Input
            value={pair.value}
            onChange={(e) => handleValueChange(pair.id, e.target.value)}
            placeholder={valuePlaceholder}
            aria-label={t("schemaAdmin.keyValue.value")}
            className="font-mono flex-1"
            style={{ width: "45%" }}
          />
          <Button
            variant="text"
            danger
            icon={<Trash2 size={14} />}
            onClick={() => handleRemove(pair.id)}
            className="flex-shrink-0"
            aria-label={t("schemaAdmin.keyValue.removeRow")}
          />
        </Compact>
      ))}
      <Button variant="dashed" size="small" icon={<Plus size={14} />} onClick={handleAdd} className="w-full">
        {addLabel ?? t("schemaAdmin.keyValue.add")}
      </Button>
    </div>
  );
}
